import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  Settings, 
  MessageCircle,
  LifeBuoy,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Info,
  Mail,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import ApiService from '../../services/ApiService';

// StatusCard component for displaying query status cards
const StatusCard = ({ icon: IconComponent, title, value, color = "blue", changeType = "neutral", onClick }) => {
  const colorClasses = {
    blue: "from-blue-500 to-indigo-600",
    green: "from-green-500 to-emerald-600", 
    orange: "from-orange-500 to-amber-600",
    gray: "from-gray-500 to-slate-600"
  };

  const changeColors = {
    increase: 'bg-emerald-100 text-emerald-700',
    decrease: 'bg-red-100 text-red-700',
    neutral: 'bg-gray-100 text-gray-700'
  };

  return (
    <div
      className={`group relative bg-white/80 backdrop-blur-sm overflow-hidden shadow-lg rounded-2xl border border-gray-200/50 hover:shadow-xl hover:border-gray-300/50 transition-all duration-300 hover:-translate-y-1 ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50/50 to-white/50"></div>
      <div className="relative p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`flex-shrink-0 p-3 bg-gradient-to-br ${colorClasses[color]} rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-200`}>
              <IconComponent className="h-6 w-6 text-white" />
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-600 mb-1">
                {title}
              </dt>
              <dd className="text-2xl font-bold text-gray-900">
                {value}
              </dd>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function Overview() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

  useEffect(() => {
    loadOverview();
  }, []);

  const loadOverview = async () => {
    try {
      setLoading(true);
      const data = await ApiService.getDashboardOverview();
      setOverview(data);
    } catch (error) {
      console.error('Failed to load overview:', error);
      toast.error('Failed to load dashboard overview');
    } finally {
      setLoading(false);
    }
  };
const handleStatClick = (href) => {
    navigate(href);
  };
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  const stats = [
    {
      name: 'Content Items',
      value: overview?.content_count || 0,
      icon: BookOpen,
      href: '/knowledge',
    },
    {
      name: 'FAQs Generated',
      value: overview?.faq_count || 0,
      icon: MessageCircle,
      href: '/optimization',
    },
    {
      name: 'Active Widgets',
      value: overview?.widget_count || 0,
      icon: Settings,
      href: '/widgets',
    },
  ];
  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="relative bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 rounded-2xl shadow-xl p-8 text-white overflow-hidden">
        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-32 h-32 bg-indigo-300/20 rounded-full blur-2xl"></div>
        <div className="relative z-10">
          <h2 className="text-3xl font-bold mb-3">Welcome to AEO Platform</h2>
          <p className="text-blue-100 text-lg leading-relaxed">
            Your AI-powered customer engagement solution is ready to help you provide exceptional support.
          </p>
          {/* <div className="mt-6 inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg text-sm font-medium text-blue-100">
            Organization ID: {overview?.organization_id || 'Loading...'}
          </div> */}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 cursor-pointer">
        {stats.map((item, index) => (
          <div 
            key={index} 
            onClick={() => handleStatClick(item.href)}
            className="group relative bg-white/80 backdrop-blur-sm overflow-hidden shadow-lg rounded-2xl border border-gray-200/50 hover:shadow-xl hover:border-gray-300/50 transition-all duration-300 hover:-translate-y-1"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-gray-50/50 to-white/50"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0 p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-200">
                    <item.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-600 mb-1">
                      {item.name}
                    </dt>
                    <dd className="text-2xl font-bold text-gray-900">
                      {item.value.toLocaleString()}
                    </dd>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions and Status Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Status Cards */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Query Status
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <StatusCard
                title="Total Queries"
                value={overview?.total_queries || 0}
                icon={Mail}
                color="blue"
                onClick={() => navigate('/support?status=all')}
              />
              <StatusCard
                title="Responded"
                value={overview?.responded_queries || 0}
                icon={CheckCircle}
                color="green"
                onClick={() => navigate('/support?status=responded')}
              />
              <StatusCard
                title="Pending"
                value={overview?.pending_queries || 0}
                icon={Clock}
                color="orange"
                onClick={() => navigate('/support?status=pending')}
              />
              <StatusCard
                title="Closed"
                value={overview?.closed_queries || 0}
                icon={XCircle}
                color="gray"
                onClick={() => navigate('/support?status=closed')}
              />
            </div>
            
            {/* Stacked Progress Bar */}
            <div className="mt-6">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span>Query Distribution</span>
                <span>{overview?.total_queries || 0} Total</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div className="h-full flex">
                  {/* Responded (Green) */}
                  <div 
                    className="bg-green-500 h-full transition-all duration-500"
                    style={{ 
                      width: overview?.total_queries > 0 
                        ? `${((overview?.responded_queries || 0) / overview.total_queries) * 100}%` 
                        : '0%' 
                    }}
                    title={`Responded: ${overview?.responded_queries || 0}`}
                  ></div>
                  {/* Pending (Orange) */}
                  <div 
                    className="bg-orange-500 h-full transition-all duration-500"
                    style={{ 
                      width: overview?.total_queries > 0 
                        ? `${((overview?.pending_queries || 0) / overview.total_queries) * 100}%` 
                        : '0%' 
                    }}
                    title={`Pending: ${overview?.pending_queries || 0}`}
                  ></div>
                  {/* Closed (Gray) */}
                  <div 
                    className="bg-gray-500 h-full transition-all duration-500"
                    style={{ 
                      width: overview?.total_queries > 0 
                        ? `${((overview?.closed_queries || 0) / overview.total_queries) * 100}%` 
                        : '0%' 
                    }}
                    title={`Closed: ${overview?.closed_queries || 0}`}
                  ></div>
                </div>
              </div>
              
              {/* Legend */}
              <div className="flex items-center justify-center space-x-4 mt-3 text-xs">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-1"></div>
                  <span className="text-gray-600">Responded ({overview?.responded_queries || 0})</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mr-1"></div>
                  <span className="text-gray-600">Pending ({overview?.pending_queries || 0})</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-gray-500 rounded-full mr-1"></div>
                  <span className="text-gray-600">Closed ({overview?.closed_queries || 0})</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Quick Actions */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Quick Actions
            </h3>
            <div className="grid grid-cols-1 gap-4">
              <QuickActionCard
                title="Upload Content"
                description="Add new content to generate FAQs"
                action="Go to Knowledge Vault"
                href="/knowledge"
                color="blue"
              />
              <QuickActionCard
                title="Create Widget"
                description="Deploy widgets on your website"
                action="Go to Widget Manager"
                href="/widgets"
                color="blue"
              />
              <QuickActionCard
                title="View Analytics"
                description="Check your AI traffic insights"
                action="Go to Analytics"
                href="/analytics"
                color="blue"
              />
            </div>
          </div>
        </div>

        
      </div>

      {/* System Status */}
      
    </div>
  );
}

function QuickActionCard({ title, description, action, href, color }) {
  const navigate = useNavigate();
  
  const colorClasses = {
    blue: 'border-blue-200 bg-blue-50 hover:bg-blue-100',
    green: 'border-blue-200 bg-blue-50 hover:bg-blue-100',
    purple: 'border-blue-200 bg-blue-50 hover:bg-blue-100',
  };

  const handleClick = (e) => {
    e.preventDefault();
    navigate(href);
  };

  return (
    <div className={`border rounded-lg p-4 transition-colors duration-200 ${colorClasses[color]}`}>
      <h4 className="font-medium text-gray-900">{title}</h4>
      <p className="text-sm text-gray-600 mt-1">{description}</p>
      <button 
        onClick={handleClick}
        className={`inline-block mt-3 text-sm font-medium text-${color}-600 hover:text-${color}-800 bg-transparent border-none cursor-pointer`}
      >
        {action} →
      </button>
    </div>
  );
}

function StatusItem({ name, status }) {
  const statusColor = status === 'operational' ? 'green' : 'red';
  
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-600">{name}</span>
      <div className="flex items-center">
        <div className={`w-2 h-2 bg-${statusColor}-400 rounded-full mr-2`}></div>
        <span className={`text-sm text-${statusColor}-600 capitalize`}>{status}</span>
      </div>
    </div>
  );
}

export default Overview;
